import PropTypes from "prop-types";
import * as React from "react";
import { Influencer } from "../../../domain";
import { NumberInput } from "../../../themeV2";
import {
  MAX_DEADLINE_DAYS,
  MIN_DEADLINE_DAYS,
  deadlineDaysOf,
  parseDeadlineDays,
} from "./completionDeadline";

/*
 * How long a shop has to fulfill an order. Committed when focus leaves the
 * field rather than per keystroke, since every save is a PATCH and a
 * partly-typed number is not a value anyone meant. An invalid entry keeps its
 * reason on screen so it can be corrected instead of being silently reverted.
 */
export default function CompletionDeadlineField({ influencer, refresh }) {
  const saved = deadlineDaysOf(influencer);

  const [draft, setDraft] = React.useState(saved);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    setDraft(saved);
    setError("");
  }, [saved]);

  const commit = React.useCallback(async () => {
    const result = parseDeadlineDays(draft);

    if (!result.ok) {
      setError(result.error);
      return;
    }

    setError("");

    if (result.days === saved) {
      return;
    }

    await Influencer.update({
      influencer,
      updates: { completion_deadline: result.days },
    });

    refresh();
  }, [draft, saved, influencer, refresh]);

  // The steppers live inside this wrapper, so stepping is not "leaving the
  // field" — only focus landing outside it commits.
  const handleBlur = React.useCallback(
    (evt) => {
      if (evt.currentTarget.contains(evt.relatedTarget)) {
        return;
      }

      commit();
    },
    [commit],
  );

  return (
    <div onBlur={handleBlur}>
      <NumberInput
        label="Completion deadline"
        description="Days a shop has to fulfill an order"
        value={draft}
        onValueChange={setDraft}
        min={MIN_DEADLINE_DAYS}
        max={MAX_DEADLINE_DAYS}
        allowOutOfRange
        step={1}
        error={error || undefined}
      />
    </div>
  );
}

CompletionDeadlineField.propTypes = {
  influencer: PropTypes.instanceOf(Influencer).isRequired,
  refresh: PropTypes.func.isRequired,
};
